import { api, SITE_ID as siteId, apiRTNotify, apiRTNotifyKey, per_page, MESSAGE_SLS_URI } from '../config/Master';
import axios, { AxiosPromise, CancelTokenSource } from 'axios';
import { getAccessToken } from '../utils/tokenUtils';

const postMessage = (hashId: string, message: string, type = '', extension: any = null): AxiosPromise => {
  const buildMessageData = (): any => {
    if (type === 'emoji' || type === 'photo') {
      return {
        hash_id: hashId,
        message,
        type,
        extension,
      };
    }
    return {
      hash_id: hashId,
      message,
    };
  };

  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'POST',
    url: `${MESSAGE_SLS_URI}/conversations`,
    data: buildMessageData(),
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const sendPoke = (targetId: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/poke/${targetId}`,
  });
};

/**
    Toggles/untoggled archive related to thread.
**/
const toggleArchive = (token: any, hashId: string | number, isArchive = true): AxiosPromise => {
  const archiveAction = isArchive ? 'unarchive' : 'archive';
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'PUT',
    url: `${MESSAGE_SLS_URI}/conversations/${hashId}/${archiveAction}`,
    data: {},
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const sendTypingIndicator = (recipientHashId: string, threadUid: string | null | undefined): AxiosPromise => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'POST',
    url: `${apiRTNotify}/typing-indicator`,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-api-key': apiRTNotifyKey,
      'x-site-id': siteId,
    },
    data: {
      receiverId: recipientHashId,
      threadUid: threadUid ?? null,
    },
  });
};

const deletePhotoMessage = (
  option: any,
  messageID: string | number,
  threadUid?: string | number | undefined
): AxiosPromise => {
  const url = `${MESSAGE_SLS_URI}/conversations/${threadUid}/messages/${messageID}`;
  const requestMethod = option === 'self' ? 'POST' : 'DELETE';
  const requestUrl = option === 'self' ? `${url}/hide` : url;
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: requestMethod,
    url: requestUrl,
    data: {
      option,
    },
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const reportPhotoMessage = (messageID: string | number): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/message/${messageID}/report`,
  });
};

const getMessagingConversationList = (
  type = 'recent',
  page = 1,
  source: CancelTokenSource | null,
  perPage = per_page
): AxiosPromise => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'GET',
    url: `${MESSAGE_SLS_URI}/conversations?mailbox=${type}&page=${page}&per_page=${perPage}`,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const getMessagingConversationByUid = (uid: string, page = 1, perPage = per_page): AxiosPromise => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'GET',
    url: `${MESSAGE_SLS_URI}/conversations/${uid}?page=${page}&per_page=${perPage}`,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const preUploadPhotoMessage = (threadUid: string, extension: string): AxiosPromise => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    url: `${MESSAGE_SLS_URI}/conversations/${threadUid}/photo?extension=${extension}`,
  });
};

const getUnreadCount = async (): Promise<number> => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      return 0;
    }

    const response = await axios({
      method: 'GET',
      url: `${MESSAGE_SLS_URI}/counters`,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response.data.data.unread_messages;
  } catch (error) {
    console.log(error);

    return 0;
  }
};

export {
  postMessage,
  sendPoke,
  toggleArchive,
  sendTypingIndicator,
  deletePhotoMessage,
  reportPhotoMessage,
  getMessagingConversationList,
  getMessagingConversationByUid,
  preUploadPhotoMessage,
  getUnreadCount,
};
